import { TenantFeatureToggle, TenantType } from '@xeetr/types/enums'
import { Settings } from '@xeetr/types/models'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import useFeatureToggle from '../../../hooks/common/useFeatureToggle'
import LabeledInputNumber from '../../inputs/LabeledInputNumber'
import LabeledInputSwitch from '../../inputs/LabeledInputSwitch'

import { useTenant } from '@/hooks/useTenant'
import { cn } from '@/lib/utils'

const TenantDashboard = () => {
  const { control } = useFormContext<Settings>()

  const isHighlightOutdated = useWatch({
    control,
    name: 'tenant.settings.highlightOutdated',
  })

  const { tenant } = useTenant()

  const markMultipleUsedTables =
    useFeatureToggle(TenantFeatureToggle.MULTIPLEBOOKEDTABLES) && tenant?.type === TenantType.FOOD

  return (
    <>
      {/* Refresh Settings Section */}
      <section>
        <div className="mb-6">
          <h2 className="text-lg font-semibold">Aktualisierung</h2>
          <p className="text-muted-foreground text-sm">
            Konfigurieren Sie, wie oft das Dashboard aktualisiert wird.
          </p>
        </div>
        <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
          <Controller
            name="tenant.settings.refreshDuration"
            control={control}
            render={({ field }) => (
              <LabeledInputNumber
                label="Aktualisierungsinterval"
                className="w-min"
                inputId={field.name}
                inputRef={field.ref}
                value={field.value}
                min={1}
                suffix="s"
                onChange={field.onChange}
              />
            )}
          />
        </div>
      </section>

      {/* Outdated Bookings Section */}
      <section>
        <div className="mb-6">
          <h2 className="text-lg font-semibold">Überfällige Reservierungen</h2>
          <p className="text-muted-foreground text-sm">
            Markieren Sie Reservierungen, die eine bestimmte Zeit überschritten haben.
          </p>
        </div>
        <div className="space-y-6">
          <Controller
            name="tenant.settings.highlightOutdated"
            control={control}
            render={({ field }) => (
              <LabeledInputSwitch
                checked={field.value ?? false}
                name={field.name}
                ref={field.ref}
                label="Überfällige Reservierungen markieren"
                onValueChange={field.onChange}
              />
            )}
          />
          <Controller
            name="tenant.settings.thresholdOutdated"
            control={control}
            render={({ field }) => (
              <LabeledInputNumber
                label="Zeit bis Reservierungen markiert werden"
                className="w-min"
                labelClassName={cn({ invisible: !isHighlightOutdated })}
                inputId={field.name}
                inputRef={field.ref}
                value={field.value}
                suffix="min"
                min={0}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      </section>

      {/* Table Booking Section (Food Tenants Only) */}
      {markMultipleUsedTables && (
        <section>
          <div className="my-6">
            <h2 className="text-lg font-semibold">Tischbuchungen</h2>
            <p className="text-muted-foreground text-sm">
              Optionen für die Verwaltung von Tischreservierungen.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Controller
              name="tenant.settings.markMultipleUsedTables"
              control={control}
              render={({ field }) => (
                <LabeledInputSwitch
                  checked={field.value ?? false}
                  name={field.name}
                  ref={field.ref}
                  label="Doppelt gebuchte Tische markieren"
                  onValueChange={field.onChange}
                />
              )}
            />
          </div>
        </section>
      )}
      <section>
        <div className="my-6">
          <h2 className="text-lg font-semibold">Zeitangabe</h2>
          <p className="text-muted-foreground text-sm">Zeigt die Uhrzeit im Kalendar an.</p>
        </div>
        <div className="space-y-6">
          <Controller
            name="tenant.settings.showTimeInDashboard"
            control={control}
            render={({ field }) => (
              <LabeledInputSwitch
                checked={field.value ?? false}
                name={field.name}
                ref={field.ref}
                label="Zeitangabe im Kalendar anzeigen"
                onValueChange={field.onChange}
              />
            )}
          />
        </div>
      </section>
      <section>
        <div className="my-6">
          <h2 className="text-lg font-semibold">Link-Freigabe</h2>
          <p className="text-muted-foreground text-sm">
            Ermöglicht das Teilen der Dashboard-Ansicht über einen Link.
          </p>
        </div>
        <div className="space-y-6">
          <Controller
            name="tenant.settings.showCopyLinkInDashboard"
            control={control}
            render={({ field }) => (
              <LabeledInputSwitch
                checked={field.value ?? false}
                name={field.name}
                ref={field.ref}
                label="Link-Kopieren-Button im Dashboard anzeigen"
                onValueChange={field.onChange}
              />
            )}
          />
        </div>
      </section>
      <section>
        <div className="my-6">
          <h2 className="text-lg font-semibold">Statistiken</h2>
          <p className="text-muted-foreground text-sm">
            Zeigt Zusammenfassungen der Buchungsdaten im Dashboard an.
          </p>
        </div>
        <div className="space-y-6">
          <Controller
            name="tenant.settings.showStatisticsInDashboard"
            control={control}
            render={({ field }) => (
              <LabeledInputSwitch
                checked={field.value ?? false}
                name={field.name}
                ref={field.ref}
                label="Statistiken im Dashboard anzeigen"
                onValueChange={field.onChange}
              />
            )}
          />
        </div>
      </section>
    </>
  )
}

export default TenantDashboard
